import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Pages from "./pages";
import styled from "styled-components";
import Components from "./components";
import routesConfig from "./routesConfig";
import { CircularProgress, CssBaseline, Paper, Stack } from "@mui/material";
import { HelmetProvider } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "./features/user/userSlice";
import PanelPage from "./pages/Panel";
import NotFoundPage from "./pages/NotFound";
import ProtectedRoute, { UnAuthRoute } from "./components/ProtectedRoute";
import { loadSettings } from "./features/app/appSlice";
import Role from "./enum/Role.enum";
import { AppDispatch, RootState } from "./store";

import LeftMenuNew from "./components/LeftMenuNew";
import AppLocalizationProvider from "./providers/AppLocalizationProvider";
import AppThemeProvider from "./providers/AppThemeProvider";
import AccountActivationBanner from "./components/AccountActivationBanner";

import "dayjs/locale/ru";
import "dayjs/locale/en";
import "dayjs/locale/tr";
import "dayjs/locale/ar";
import "dayjs/locale/hy-am";

const { Width, Header, Footer } = Components;

const ContentWrapper = styled(Width)`
  display: flex;
  padding: 40px 0;
  height: auto;
  flex: 1;
`;

const Content = styled(Paper)`
  width: auto;
  flex: 1;
  padding: 24px 32px;
  margin-inline-start: 30px;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 8px;
`;

const AppRoutes = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const isIndexPage = location.pathname === "/";

  const { roles: userRoles } = useSelector((state: RootState) => state.user);
  const provider = useSelector((state: RootState) => state.app.provider);
  const settingsLoading = useSelector(
    (state: RootState) => state.app.settingsLoading,
  );

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        dispatch(setUser(user));
        dispatch(loadSettings());
      } catch (e) {
        console.error("Ошибка разбора user из localStorage", e);
      }
    }
  }, []);

  useEffect(() => {
    if (userRoles.includes(Role.RESET_PASSWORD)) {
      navigate("/change_password");
    }
  }, [userRoles]);

  if (isIndexPage) {
    return (
      <Routes>
        <Route
          path="/"
          element={<UnAuthRoute component={<Pages.Welcome />} />}
        />
      </Routes>
    );
  }

  if (settingsLoading) {
    return (
      <Stack justifyContent="center" alignItems="center" sx={{ m: 3 }}>
        <CircularProgress color="primary" size={34} thickness={2.5} />
      </Stack>
    );
  }

  return (
    <ContentWrapper>
      <LeftMenuNew />

      <Content>
        <AccountActivationBanner />

        <Routes>
          {routesConfig.map((route) => {
            const hasAccess = route.requiredRoles.some((role) =>
              userRoles.includes(role),
            );
            const hasProvider =
              !route.requiredProviders ||
              route.requiredProviders.length === 0 ||
              route.requiredProviders.includes(provider);

            if (hasAccess && hasProvider) {
              let RoutesArr: any[] = [];
              if (route.componentPath) {
                const Component = Pages[route.componentPath];
                RoutesArr = RoutesArr.concat([
                  <Route
                    key={route.path}
                    path={route.path}
                    element={
                      <ProtectedRoute
                        component={<Component />}
                        routePath={route.path}
                      />
                    }
                  />,
                ]);
              }
              if (route.children) {
                const childrenRoutes = route.children.map((subRoute) => {
                  const SubComponent = Pages[subRoute.componentPath];
                  return (
                    <Route
                      key={subRoute.path}
                      path={subRoute.path}
                      element={
                        <ProtectedRoute
                          component={<SubComponent />}
                          routePath={subRoute.path}
                        />
                      }
                    />
                  );
                });
                RoutesArr = RoutesArr.concat(childrenRoutes);
              }
              return RoutesArr;
            }
            return null;
          })}

          <Route
            path="/panel"
            element={
              <ProtectedRoute component={<PanelPage />} routePath="/panel" />
            }
          />
          <Route
            path="*"
            element={<ProtectedRoute component={<NotFoundPage />} />}
          />
        </Routes>
      </Content>
    </ContentWrapper>
  );
};

const App = () => {
  return (
    <AppLocalizationProvider>
      <HelmetProvider>
        <AppThemeProvider>
          <CssBaseline />
          <Header />
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <AppRoutes />
          </Router>
          <Footer />
        </AppThemeProvider>
      </HelmetProvider>
    </AppLocalizationProvider>
  );
};

export default App;
